import type { RefObject } from "react";
import { getScrollMaxTop } from "./scroll-controller-animation";
import { LATEST_MESSAGE_ANCHOR_OFFSET_TOP } from "./scroll-controller-constants";
import { getRegisteredScrollTop } from "./scroll-controller-target";

interface ResolveLatestMessageAnchorTopInput {
  el: HTMLDivElement;
  messageElementsRef: RefObject<Map<string, HTMLElement>>;
}

/**
 * Resolves the scrollTop that anchors the newest (bottom-most) message's top near
 * the viewport top, leaving the rest of that message to extend downward.
 *
 * The animation layer clamps the returned value to the maximum scroll position, so
 * a newest message shorter than the viewport can't be pulled all the way up and
 * naturally settles at the bottom — the same place the view sat before this change.
 * Falls back to the absolute bottom when no message elements are registered.
 */
export function resolveLatestMessageAnchorTop({
  el,
  messageElementsRef,
}: ResolveLatestMessageAnchorTopInput): number {
  const latestMessageElement = findBottomMostMessageElement(messageElementsRef);
  if (!latestMessageElement) {
    return getScrollMaxTop(el);
  }

  return getRegisteredScrollTop({
    targetElement: latestMessageElement,
    offsetTop: LATEST_MESSAGE_ANCHOR_OFFSET_TOP,
  });
}

function findBottomMostMessageElement(
  messageElementsRef: RefObject<Map<string, HTMLElement>>,
): HTMLElement | null {
  return [...messageElementsRef.current.values()].reduce<HTMLElement | null>(
    (bottomMost, candidate) => {
      if (!candidate.isConnected) {
        return bottomMost;
      }
      if (!bottomMost || candidate.offsetTop > bottomMost.offsetTop) {
        return candidate;
      }
      return bottomMost;
    },
    null,
  );
}
